import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { BookOpen, Monitor, Shield } from 'lucide-react';

export default function UsageManualPage() {
  const { isStaff, isClient } = useAuth();
  const [tab, setTab] = useState(isStaff ? 'staff' : 'client');

  return (
    <div>
      <div className="page-header">
        <h1>Usage Manual</h1>
        <p>How to use the Axslogic Support Portal</p>
      </div>

      {/* Tab switcher — staff can see both; clients see only their tab */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid var(--color-border)' }}>
        <TabBtn id="client" icon={Monitor} label="Client User Guide" active={tab} onClick={setTab} />
        {isStaff && <TabBtn id="staff" icon={Shield} label="Axslogic Staff Guide" active={tab} onClick={setTab} />}
      </div>

      {tab === 'client' && <ClientGuide />}
      {tab === 'staff' && isStaff && <StaffGuide />}
    </div>
  );
}

function TabBtn({ id, icon: Icon, label, active, onClick }) {
  return (
    <button onClick={() => onClick(id)} style={{
      padding: '8px 16px', border: 'none', background: 'none', fontSize: 13, fontWeight: 500,
      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
      color: active === id ? 'var(--color-primary)' : 'var(--color-text-muted)',
      borderBottom: active === id ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: -2,
    }}>
      <Icon size={14} /> {label}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════
// Client User Guide
// ═══════════════════════════════════════════════════════════
function ClientGuide() {
  return (
    <div style={{ maxWidth: 760 }}>
      <Section title="Getting Started">
        <P>The Axslogic Support Portal is your 24×7 channel for raising and tracking support tickets for the Winnow Analytics Platform. You can report issues, request data reloads, ask for assistance, and track resolution progress — all from a single interface.</P>
        <P>Log in with the email and password provided by Axslogic. If you don't have credentials, contact Axslogic to raise a request.</P>
      </Section>

      <Section title="Raising a Ticket">
        <Step n={1} title="Navigate to 'Raise ticket'">Click <strong>Raise ticket</strong> in the left sidebar.</Step>
        <Step n={2} title="Select the issue type">Choose the type that best describes your request. The form fields will adjust automatically based on your selection — some fields may appear, disappear, or become required depending on the issue type.</Step>
        <Step n={3} title="Fill in the details">
          <ul style={ulStyle}>
            <li><strong>Feature / Sub-feature</strong> — the Winnow feature area affected (when shown)</li>
            <li><strong>Module / Mart</strong> — the data module and mart involved (when shown)</li>
            <li><strong>Description fields</strong> — labels change per issue type. Provide as much detail as possible.</li>
            <li><strong>Impact &amp; Severity</strong> — some issue types auto-set these; others require your selection</li>
          </ul>
        </Step>
        <Step n={4} title="Attach supporting files">Drag and drop screenshots, logs, or documents into the upload area. Max 10 MB per file.</Step>
        <Step n={5} title="Submit">Click <strong>Submit ticket</strong>. You'll receive an email confirmation with the ticket ID.</Step>
      </Section>

      <Section title="Tracking Your Tickets">
        <P>Go to <strong>Tickets</strong> in the sidebar to see all your tickets. Use the tabs to switch between <strong>Active</strong>, <strong>Closed</strong>, and <strong>All</strong> views. You can also search by ticket ID, feature, or module.</P>
        <P>Click any ticket to see its full detail, including current status, assigned team, timeline, and any resolution notes.</P>
      </Section>

      <Section title="Understanding Ticket Status">
        <StatusTable items={[
          ['Open', 'Your ticket has been submitted and is awaiting triage.'],
          ['In Progress', 'The Axslogic team is working on it (internal statuses are simplified for you).'],
          ['Resolution Completed', 'A fix has been applied and verified by the Axslogic team.'],
          ['Communicated', 'The resolution has been communicated to you. You can now close or reject.'],
          ['Closed', 'You have accepted the resolution. The ticket is complete.'],
        ]} />
      </Section>

      <Section title="Closing or Rejecting a Resolution">
        <P>When a ticket reaches <strong>Communicated</strong> status, you'll see two buttons on the ticket detail page:</P>
        <ul style={ulStyle}>
          <li><strong>Close ticket</strong> — accept the resolution and close the ticket.</li>
          <li><strong>Reject</strong> — if the fix didn't work. Enter a rejection reason (required). The ticket goes back to the assigned team for re-work.</li>
        </ul>
      </Section>

      <Section title="Withdrawing a Ticket">
        <P>If you raised a ticket by mistake, you can <strong>withdraw</strong> it while it's still in <strong>Open</strong> status. Once triage begins, contact the support team to cancel.</P>
      </Section>

      <Section title="Changing Your Password">
        <P>Click <strong>Change password</strong> in the sidebar. Your new password must be at least 10 characters with a mix of uppercase, lowercase, digits, and special characters.</P>
      </Section>

      <Section title="Session Timeout">
        <P>For security, the portal automatically logs you out after <strong>10 minutes of inactivity</strong>. Any mouse movement, keyboard input, or scrolling resets the timer.</P>
      </Section>

      <Section title="SLA & Holidays">
        <P>Each ticket has an SLA deadline based on its severity level. The deadline is computed in business hours — weekends and <strong>Axslogic holidays</strong> are excluded (since Axslogic staff work the ticket). You can view the Axslogic holiday calendar from the <strong>Holiday calendar</strong> page in the sidebar.</P>
      </Section>

      <Section title="Need Help?">
        <P>If you have questions about the portal itself, contact Axslogic or raise an <strong>Assistance</strong> type ticket.</P>
      </Section>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Axslogic Staff Guide
// ═══════════════════════════════════════════════════════════
function StaffGuide() {
  return (
    <div style={{ maxWidth: 760 }}>
      <Section title="Authentication">
        <P>Staff authenticate via <strong>Microsoft 365 SSO</strong>. Click "Sign in with Microsoft" on the login page. Your role and group memberships are determined by your MS365 ITRK_ groups.</P>
        <P>In development mode (DEBUG=true), use the <strong>Dev mode (staff testing)</strong> button to log in as any role without MS365.</P>
      </Section>

      <Section title="Role Hierarchy">
        <StatusTable items={[
          ['CTO / Admin', 'Full access. Can triage, assign groups, assign individuals in any group, force-close tickets.'],
          ['Support Lead', 'Can triage tickets, assign groups, view all tickets, manage configuration.'],
          ['Team Lead (QA/Engg/DevOps/ETL)', 'Can assign individuals within their own group. See all tickets in their group.'],
          ['Team Member', 'See tickets assigned to them + unassigned tickets in their group queue.'],
        ]} />
      </Section>

      <Section title="Ticket Workflow">
        <Step n={1} title="Triage (Support Lead / CTO)">Review the ticket, confirm it's valid. Click <strong>Mark as triaged</strong>.</Step>
        <Step n={2} title="Assign Groups (Support Lead / CTO)">Select a resolver group and a verifier group. These can be the same group (peer review) or different groups (cross-team verification).</Step>
        <Step n={3} title="Assign Individuals (Team Leads)">Each group lead assigns the individual from their team. The resolver group lead assigns the resolver; the verifier group lead assigns the verifier. If both groups are the same, one lead handles both. Status advances only when both individuals are assigned.</Step>
        <Step n={4} title="Commence Resolution (Resolver)">The assigned resolver sets an EDC (Expected Date of Completion) and begins work. The system warns if the EDC exceeds the SLA deadline.</Step>
        <Step n={5} title="Complete Resolution (Resolver)">Document the resolution steps and root cause. Attach evidence files.</Step>
        <Step n={6} title="Verify (Verifier)">The assigned verifier confirms the fix is effective. Attach verification evidence. Once verified, the resolution is automatically communicated to the client.</Step>
        <Step n={7} title="Close / Reject (Client)">The client either closes the ticket (success) or rejects with a reason (re-work). Rejection resets to group-assigned state with the same groups — the lead re-assigns individuals.</Step>
      </Section>

      <Section title="Configuration (Admin / Support Lead)">
        <P>Access <strong>Configuration</strong> from the sidebar. Tabs include:</P>
        <ul style={ulStyle}>
          <li><strong>Issue types</strong> — add, edit, deactivate issue types</li>
          <li><strong>Features</strong> — hierarchical feature/sub-feature tree with edit and delete</li>
          <li><strong>Client SLA</strong> — per-client SLA targets by severity (response time, fix time, thresholds)</li>
          <li><strong>Assignment rules</strong> — matrix of which roles can assign to which groups</li>
          <li><strong>Escalation matrix</strong> — per-client escalation thresholds (P1-P4 × L1-L4) with explicit save buttons</li>
          <li><strong>Role mappings</strong> — map Axslogic roles to client-side escalation contacts</li>
          <li><strong>Hub sync</strong> — trigger manual sync of client metadata (modules, marts, holidays) from the DevOps Hub</li>
        </ul>
      </Section>

      <Section title="Dashboard">
        <P>The dashboard shows ticket volume stats, monthly trend charts, SLA compliance bars, and a per-client breakdown table. Use the client selector to filter telemetry to a specific client.</P>
      </Section>

      <Section title="Ticket Visibility Rules">
        <ul style={ulStyle}>
          <li><strong>Admin / CTO / Support Lead</strong> — see all tickets across all clients</li>
          <li><strong>Team Lead</strong> — see all tickets assigned to their group(s)</li>
          <li><strong>Team Member</strong> — see tickets assigned to them individually, plus unassigned tickets in their group queue</li>
          <li><strong>Client users</strong> — see only their own client's tickets</li>
        </ul>
      </Section>

      <Section title="Dev-Login Presets">
        <P>When testing locally (DEBUG=true), use these email presets to log in as different roles:</P>
        <StatusTable items={[
          ['supp.lead@axslogic.com', 'Support Lead (SUPPORT group, lead)'],
          ['engg.lead@axslogic.com', 'Engineering Lead (ENGINEERING group, lead)'],
          ['engg1@axslogic.com', 'Engineer (ENGINEERING group, member)'],
          ['qa.lead@axslogic.com', 'QA Lead (QA group, lead)'],
          ['cto@axslogic.com', 'CTO (bypasses group checks)'],
        ]} />
      </Section>
    </div>
  );
}

// ── Shared sub-components ──

function Section({ title, children }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-title" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <BookOpen size={16} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
        {title}
      </div>
      {children}
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
      <div style={{
        width: 24, height: 24, borderRadius: '50%', background: 'var(--color-primary-light)',
        color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 2,
      }}>{n}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>{children}</div>
      </div>
    </div>
  );
}

function P({ children }) {
  return <p style={{ fontSize: 13, color: 'var(--color-text)', lineHeight: 1.7, marginBottom: 10 }}>{children}</p>;
}

function StatusTable({ items }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 8 }}>
      <tbody>
        {items.map(([left, right], idx) => (
          <tr key={idx} style={{ borderBottom: '1px solid var(--color-border)' }}>
            <td style={{ padding: '8px 12px 8px 0', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top', width: 1 }}>{left}</td>
            <td style={{ padding: '8px 0', color: 'var(--color-text-muted)' }}>{right}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const ulStyle = { paddingLeft: 20, marginBottom: 10, fontSize: 13, lineHeight: 1.8, color: 'var(--color-text-muted)' };
